import React, { useEffect, useState } from 'react';
import {
  Page,
  Card,
  Badge,
  Text,
  BlockStack,
  InlineStack,
  InlineGrid,
  Spinner,
  Button,
  Banner,
  TextField,
} from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';

interface Plan {
  id: 'free' | 'paid';
  name: string;
  price: number;
  priceType: string;
  maxProducts?: number | null; // null = ilimitado
  description?: string;
  features: string[];
}

interface ShopifyConnection {
  connected: boolean;
  details?: {
    shopName?: string;
    shopDomain?: string;
    plan?: string;
    currency?: string;
    billingApiAccess?: boolean;
    activeCharges?: number;
    billingError?: string;
  };
  error?: string;
  testShop?: string;
}

const AdminPlans: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [testingShopify, setTestingShopify] = useState(false);
  const [plans, setPlans] = useState<{ free: Plan; paid: Plan } | null>(null);
  const [shopifyConnection, setShopifyConnection] = useState<ShopifyConnection | null>(null);
  const [editingPlan, setEditingPlan] = useState<'free' | 'paid' | null>(null);
  const [editPrice, setEditPrice] = useState<string>('');
  const [editName, setEditName] = useState<string>('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    fetchPlans();
  }, [navigate]);

  const fetchPlans = async () => {
    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      const token = localStorage.getItem('admin_token');

      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/admin-plans-config`, {
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'X-Admin-Token': token,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPlans(data.plans);
      } else if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
      }
    } catch (error) {
      setError('Error loading plan configurations.');
    } finally {
      setLoading(false);
    }
  };

  const testShopifyConnection = async () => {
    setTestingShopify(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      const token = localStorage.getItem('admin_token');

      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(
        `${supabaseUrl}/functions/v1/admin-plans-config?action=test-shopify`,
        {
          headers: {
            'apikey': supabaseAnonKey,
            'Authorization': `Bearer ${supabaseAnonKey}`,
            'X-Admin-Token': token,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        setShopifyConnection(data);
        if (data.connected) {
          setSuccess('Conexão com Shopify API verificada com sucesso!');
          setTimeout(() => setSuccess(null), 3000);
        } else {
          setError(data.error || 'Não foi possível conectar com a API do Shopify');
        }
      } else {
        setError('Erro ao testar conexão com Shopify API');
      }
    } catch (err) {
      setError('Erro ao testar conexão. Verifique sua conexão com a internet.');
    } finally {
      setTestingShopify(false);
    }
  };

  const handleEditPlan = (planId: 'free' | 'paid') => {
    if (!plans) return;
    const plan = plans[planId];
    setEditingPlan(planId);
    setEditPrice(plan.price.toString());
    setEditName(plan.name);
    setError(null);
    setSuccess(null);
  };

  const handleSavePlan = async () => {
    if (!editingPlan) return;

    setSaving(true);
    setError(null);

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || 'https://nilhosqgvmsabnljpavo.supabase.co';
      const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || '';
      const token = localStorage.getItem('admin_token');

      const price = parseFloat(editPrice);
      if (isNaN(price) || price < 0) {
        setError('Preço inválido. Digite um número positivo.');
        setSaving(false);
        return;
      }

      if (!token) {
        navigate('/admin/login');
        setSaving(false);
        return;
      }

      const response = await fetch(`${supabaseUrl}/functions/v1/admin-plans-config`, {
        method: 'POST',
        headers: {
          'apikey': supabaseAnonKey,
          'Authorization': `Bearer ${supabaseAnonKey}`,
          'X-Admin-Token': token,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          planId: editingPlan,
          updates: {
            name: editName,
            price: price,
          },
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(data.message || 'Plano atualizado com sucesso!');
        setEditingPlan(null);
        // Recarregar planos
        fetchPlans();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const data = await response.json();
        setError(data.error || 'Erro ao salvar plano');
      }
    } catch (err) {
      setError('Erro ao salvar plano. Tente novamente.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Page>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <Spinner size="large" />
        </div>
      </Page>
    );
  }

  if (!plans) {
    return (
      <Page>
        <Banner tone="critical">
          Erro ao carregar configurações dos planos. Tente novamente.
        </Banner>
      </Page>
    );
  }

  return (
    <Page
      title="Gerenciar Planos e Preços"
      primaryAction={{
        content: 'Voltar ao Dashboard',
        onAction: () => navigate('/admin/dashboard'),
      }}
      secondaryActions={[
        {
          content: 'Testar Conexão Shopify',
          onAction: testShopifyConnection,
          loading: testingShopify,
        },
        {
          content: 'Atualizar',
          onAction: fetchPlans,
        },
        {
          content: 'Sair',
          onAction: () => {
            localStorage.removeItem('admin_token');
            navigate('/admin/login');
          },
        },
      ]}
    >
      <BlockStack gap="500">
        {success && (
          <Banner tone="success" onDismiss={() => setSuccess(null)}>
            {success}
          </Banner>
        )}

        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}

        {/* Status da Conexão Shopify */}
        <Card>
          <BlockStack gap="400">
            <InlineStack align="space-between" blockAlign="center">
              <BlockStack gap="100">
                <Text as="h2" variant="headingMd">
                  Status da Conexão Shopify API
                </Text>
                <Text as="p" tone="subdued" variant="bodySm">
                  Verifique se a integração com a API do Shopify está funcionando corretamente
                </Text>
              </BlockStack>
              <Button onClick={testShopifyConnection} loading={testingShopify}>
                Testar Conexão
              </Button>
            </InlineStack>

            {shopifyConnection && (
              <BlockStack gap="300">
                <InlineStack gap="200" blockAlign="center">
                  <Badge tone={shopifyConnection.connected ? 'success' : 'critical'}>
                    {shopifyConnection.connected ? 'Conectado' : 'Desconectado'}
                  </Badge>
                  {shopifyConnection.testShop && (
                    <Text as="p" variant="bodySm" tone="subdued">
                      Testado com: {shopifyConnection.testShop}
                    </Text>
                  )}
                </InlineStack>

                {shopifyConnection.connected && shopifyConnection.details && (
                  <BlockStack gap="200">
                    <Text as="p" variant="bodySm">
                      <strong>Loja:</strong> {shopifyConnection.details.shopName || shopifyConnection.details.shopDomain}
                    </Text>
                    <Text as="p" variant="bodySm">
                      <strong>Plano Shopify:</strong> {shopifyConnection.details.plan}
                    </Text>
                    <Text as="p" variant="bodySm">
                      <strong>Moeda:</strong> {shopifyConnection.details.currency}
                    </Text>
                    <InlineStack gap="200" blockAlign="center">
                      <Badge tone={shopifyConnection.details.billingApiAccess ? 'success' : 'critical'}>
                        {`Billing API: ${shopifyConnection.details.billingApiAccess ? 'Acessível' : 'Não Acessível'}`}
                      </Badge>
                      {shopifyConnection.details.activeCharges !== undefined && (
                        <Text as="p" variant="bodySm">
                          Charges ativas: {shopifyConnection.details.activeCharges}
                        </Text>
                      )}
                    </InlineStack>
                    {shopifyConnection.details.billingError && (
                      <Text as="p" variant="bodySm" tone="critical">
                        Erro Billing API: {shopifyConnection.details.billingError}
                      </Text>
                    )}
                  </BlockStack>
                )}

                {shopifyConnection.error && (
                  <Text as="p" variant="bodySm" tone="critical">
                    {shopifyConnection.error}
                  </Text>
                )}
              </BlockStack>
            )}
          </BlockStack>
        </Card>

        {/* Configuração dos Planos */}
        <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
          {/* Plano FREE */}
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingLg">
                  {editingPlan === 'free' ? (
                    <TextField
                      label=""
                      value={editName}
                      onChange={setEditName}
                      autoComplete="off"
                    />
                  ) : (
                    plans.free.name
                  )}
                </Text>
                {editingPlan !== 'free' && (
                  <Button size="slim" onClick={() => handleEditPlan('free')}>
                    Editar
                  </Button>
                )}
              </InlineStack>

              <BlockStack gap="200">
                <Text as="p" variant="heading2xl" tone="subdued">
                  Grátis
                </Text>
                <Text as="p" variant="bodySm" tone="subdued">
                  Para sempre - Sem período de teste!
                </Text>
                {plans.free.description && (
                  <Text as="p" variant="bodySm" tone="subdued">
                    {plans.free.description}
                  </Text>
                )}
              </BlockStack>

              {editingPlan === 'free' ? (
                <BlockStack gap="300">
                  <Button variant="primary" onClick={handleSavePlan} loading={saving}>
                    Salvar
                  </Button>
                  <Button onClick={() => setEditingPlan(null)}>
                    Cancelar
                  </Button>
                </BlockStack>
              ) : (
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                    Funcionalidades:
                  </Text>
                  {plans.free.features.map((feature, index) => (
                    <Text key={index} as="p" variant="bodySm">
                      ✅ {feature}
                    </Text>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>

          {/* Plano PRO */}
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingLg">
                  {editingPlan === 'paid' ? (
                    <TextField
                      label=""
                      value={editName}
                      onChange={setEditName}
                      autoComplete="off"
                    />
                  ) : (
                    plans.paid.name
                  )}
                </Text>
                {editingPlan !== 'paid' && (
                  <Button size="slim" onClick={() => handleEditPlan('paid')}>
                    Editar
                  </Button>
                )}
              </InlineStack>

              <BlockStack gap="200">
                {editingPlan === 'paid' ? (
                  <TextField
                    label="Preço Mensal"
                    type="number"
                    prefix="$"
                    value={editPrice}
                    onChange={setEditPrice}
                    autoComplete="off"
                  />
                ) : (
                  <>
                    <Text as="p" variant="heading2xl">
                      ${plans.paid.price.toFixed(2)}
                      <span style={{ fontSize: '1rem', fontWeight: 'normal' }}>/mês</span>
                    </Text>
                    <Text as="p" variant="bodySm" tone="subdued">
                      Cancele a qualquer momento
                    </Text>
                    {plans.paid.description && (
                      <Text as="p" variant="bodySm" tone="subdued">
                        {plans.paid.description}
                      </Text>
                    )}
                  </>
                )}
              </BlockStack>

              {editingPlan === 'paid' ? (
                <BlockStack gap="300">
                  <Text as="p" variant="bodySm" tone="subdued">
                    ⚠️ O preço configurado aqui será usado ao criar novas assinaturas. 
                    Assinaturas existentes manterão o preço original.
                  </Text>
                  <Button variant="primary" onClick={handleSavePlan} loading={saving}>
                    Salvar
                  </Button>
                  <Button onClick={() => setEditingPlan(null)}>
                    Cancelar
                  </Button>
                </BlockStack>
              ) : (
                <BlockStack gap="200">
                  <Text as="p" variant="bodyMd" fontWeight="semibold">
                    Funcionalidades:
                  </Text>
                  {plans.paid.features.map((feature, index) => (
                    <Text key={index} as="p" variant="bodySm">
                      ✅ {feature}
                    </Text>
                  ))}
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </InlineGrid>
      </BlockStack>
    </Page>
  );
};

export default AdminPlans;
